import { useMemo } from 'react';

import { FilterBar2, FilterBar2ListItem, FilterBar2Value } from '@bringg/react-components';
import moment from 'moment';
import { TFunction, useTranslation } from 'react-i18next';
import { Fleet, FleetType } from '@bringg/types';
import { sortByString } from '@bringg-frontend/utils';

import { FilterBarListItemKeys } from '../types';
import useTimeFormat from 'bringg-web/hooks/use-time-format';
import useDateFormat from 'bringg-web/hooks/use-date-format';
import { useStores } from 'bringg-web/recipes';
import {
	ServicePlansTranslation,
	defaultDateTimeDynamicRanges,
	filterBarPlaceholderTranslations,
	fleetsTranslation,
	rangeDateTranslation,
	rankTranslation,
	readyForPickupOption,
	skillsTranslation,
	unscheduledOption
} from 'bringg-web/features/filter-bar/filter-bar';
import { hasInternalFleet } from 'bringg-web/stores/fleets/fleet-checker';
import {
	runDateTranslations,
	runDriverNameTranslations,
	runIdTranslations
} from 'bringg-web/features/runs/runs-filter/runs-filter.consts';
import { monitoringFiltersProvider } from '../utils/monitoring-filter-provider';

const rankOptions = Array.from(Array(10), (_, i) => ({ value: i + 1, label: `${i + 1}` }));

const getFleets = (allFleets: Fleet[], t: TFunction) => {
	const NO_FLEET = -1;
	const ownFleetTranslation = hasInternalFleet() ? t('FILTER_BAR.NO_FLEET') : t('FILTER_BAR.FLEETS_INTERNAL');
	const fleets = allFleets
		.map(fleet => {
			const isInternalFleet = fleet.fleet_type === FleetType.Internal;
			return {
				value: fleet.id,
				label: isInternalFleet ? t('ASSIGN_FLEET.INTERNAL_FLEET_TEXT') : fleet.name
			};
		})
		.sort((firstItem, secondItem) => sortByString(firstItem, secondItem, 'name'));

	return [...fleets, { value: NO_FLEET, label: ownFleetTranslation }];
};

const useFilterBarItems = () => {
	const { t } = useTranslation();
	const { tagsStore, skillsStore, merchantConfigurationsStore, servicePlansStore, fleetsStore } = useStores();
	const hourFormat = useTimeFormat();
	const dateFormat = useDateFormat();

	const tagOptions = useMemo<FilterBar2Value[]>(() => {
		if (tagsStore.isFetched) {
			return tagsStore.getAll.map(tag => ({ label: tag.tag, value: tag.id }));
		}

		return [];
	}, [tagsStore.getAll, tagsStore.isFetched]);

	const filterBarFilters = useMemo<FilterBar2ListItem[]>(() => {
		const defaultFilters: FilterBar2ListItem[] = [
			{
				type: FilterBar2.TYPES.DATE_RANGE_V2,
				name: FilterBarListItemKeys.TIME_WINDOW,
				title: runDateTranslations(t).dates,
				tooltip: runDateTranslations(t).datesTooltip,
				customProps: {
					showTime: {
						format: hourFormat
					},
					format: `${dateFormat} ${hourFormat}`,
					ranges: defaultDateTimeDynamicRanges(t),
					placeholder: [rangeDateTranslation(t).fromDate, rangeDateTranslation(t).toDate]
				},
				applyFilterOnValueChange: true
			},
			{
				type: FilterBar2.TYPES.SELECT,
				name: FilterBarListItemKeys.DRIVERS,
				title: runDriverNameTranslations(t).driverName,
				placeholder: runDriverNameTranslations(t).driverName,
				tooltip: runDriverNameTranslations(t).driverNameTooltip,
				multiselect: true,
				customProps: {
					onSearch: monitoringFiltersProvider.handleSearch
				},
				values: [],
				applyFilterOnValueChange: true
			}
		];

		const additionalFilters: FilterBar2ListItem[] = [
			{
				type: FilterBar2.TYPES.RADIO,
				name: FilterBarListItemKeys.UNASSIGNED,
				title: filterBarPlaceholderTranslations(t).unassigned,
				placeholder: filterBarPlaceholderTranslations(t).unassigned,
				tooltip: t('FILTER_BAR.TOOLTIP.UNASSIGNED'),
				additional: true,
				values: [
					{ value: 1, label: t('FILTER_BAR.ASSIGNED_TASKS') },
					{ value: 0, label: t('FILTER_BAR.UNASSIGNED_TASKS') }
				],
				applyFilterOnValueChange: true
			},
			{
				type: FilterBar2.TYPES.SELECT,
				multiselect: true,
				name: FilterBarListItemKeys.TAGS,
				title: t('FILTER_BAR.TAGS_TEXT'),
				placeholder: t('FILTER_BAR.TAGS_TEXT'),
				values: tagOptions,
				additional: true,
				tooltip: t('FILTER_BAR.TOOLTIP.TAGS'),
				applyFilterOnValueChange: true
			},
			{
				type: FilterBar2.TYPES.SELECT,
				name: FilterBarListItemKeys.RANKS,
				title: rankTranslation(t).multiselectPlaceholder,
				placeholder: rankTranslation(t).multiselectPlaceholder,
				tooltip: t('FILTER_BAR.TOOLTIP.RANKS'),
				multiselect: true,
				additional: true,
				values: rankOptions,
				applyFilterOnValueChange: true
			},
			{
				type: FilterBar2.TYPES.SELECT,
				name: FilterBarListItemKeys.ROUTES,
				title: runIdTranslations(t).runId,
				placeholder: runIdTranslations(t).runId,
				tooltip: runIdTranslations(t).runIdTooltip,
				multiselect: true,
				additional: true,
				customProps: {
					onSearch: monitoringFiltersProvider.handleSearch
				},
				values: [],
				applyFilterOnValueChange: true
			},
			{
				type: FilterBar2.TYPES.DATE_RANGE_V2,
				name: FilterBarListItemKeys.SLA,
				title: t('FILTER_BAR.SLA_WINDOW'),
				tooltip: t('FILTER_BAR.TOOLTIP.SLA'),
				additional: true,
				customProps: {
					showTime: { format: hourFormat, defaultValue: [moment().startOf('day'), moment().endOf('day')] },
					format: `${dateFormat} ${hourFormat}`,
					ranges: defaultDateTimeDynamicRanges(t),
					placeholder: [rangeDateTranslation(t).fromDate, rangeDateTranslation(t).toDate]
				},
				applyFilterOnValueChange: true
			},
			{
				type: FilterBar2.TYPES.RADIO,
				name: FilterBarListItemKeys.UNSCHEDULED,
				title: t('FILTER_BAR.UNSCHEDULED_ORDERS'),
				placeholder: t('FILTER_BAR.UNSCHEDULED_ORDERS'),
				tooltip: t('FILTER_BAR.UNSCHEDULED_ORDERS_TOOLTIP'),
				additional: true,
				values: unscheduledOption(t),
				applyFilterOnValueChange: true
			},
			{
				type: FilterBar2.TYPES.DATE_RANGE_V2,
				name: FilterBarListItemKeys.RESERVED_UNTIL,
				additional: true,
				title: t('FILTER_BAR.RESERVED_UNTIL'),
				tooltip: t('FILTER_BAR.TOOLTIP.RESERVED_UNTIL'),
				customProps: {
					showTime: { format: hourFormat, defaultValue: [moment().startOf('day'), moment().endOf('day')] },
					format: `${dateFormat} ${hourFormat}`,
					ranges: defaultDateTimeDynamicRanges(t),
					placeholder: [rangeDateTranslation(t).fromDate, rangeDateTranslation(t).toDate]
				},
				applyFilterOnValueChange: true
			},
			{
				type: FilterBar2.TYPES.RADIO,
				name: 'readyForPickup',
				title: t('FILTER_BAR.READY_FOR_PICKUP'),
				placeholder: t('FILTER_BAR.READY_FOR_PICKUP'),
				tooltip: t('FILTER_BAR.READY_FOR_PICKUP_TOOLTIP'),
				additional: true,
				values: readyForPickupOption(t),
				applyFilterOnValueChange: true
			}
		];

		if (merchantConfigurationsStore?.enableSkills && skillsStore?.all.length) {
			additionalFilters.push({
				type: FilterBar2.TYPES.SELECT,
				name: FilterBarListItemKeys.SKILLS,
				title: skillsTranslation(t).multiselectPlaceholder,
				placeholder: skillsTranslation(t).multiselectPlaceholder,
				tooltip: t('FILTER_BAR.TOOLTIP.SKILLS'),
				multiselect: true,
				additional: true,
				values: skillsStore?.all.map(item => ({
					label: item.name,
					value: item.name
				})),
				applyFilterOnValueChange: true
			});
		}

		if (servicePlansStore.getAll.length) {
			additionalFilters.push({
				type: FilterBar2.TYPES.SELECT,
				name: FilterBarListItemKeys.SERVICE_PLANS,
				title: ServicePlansTranslation(t).multiselectPlaceholder,
				placeholder: ServicePlansTranslation(t).multiselectPlaceholder,
				tooltip: t('FILTER_BAR.TOOLTIP.SERVICE_PLANS'),
				additional: true,
				multiselect: true,
				values: servicePlansStore.getAll
					.sort((firstItem, secondItem) => sortByString(firstItem, secondItem, 'name'))
					.map(item => ({
						label: item.name,
						value: item.id
					})),
				applyFilterOnValueChange: true
			});
		}

		if (fleetsStore.all.length) {
			additionalFilters.push({
				type: FilterBar2.TYPES.SELECT,
				name: FilterBarListItemKeys.FLEETS,
				title: fleetsTranslation(t).multiselectPlaceholder,
				placeholder: fleetsTranslation(t).multiselectPlaceholder,
				tooltip: t('FILTER_BAR.TOOLTIP.FLEETS'),
				multiselect: true,
				additional: true,
				values: getFleets(fleetsStore.all, t),
				applyFilterOnValueChange: true
			});
		}

		return defaultFilters.concat(additionalFilters);
	}, []);

	return filterBarFilters;
};

export default useFilterBarItems;
